import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, BellOff, CheckCheck, ChevronRight, Inbox, Loader2, Search, X } from 'lucide-react';
import {
  useDismissNotification,
  useInboxUnreadCount,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationFeed,
} from '../../hooks/api';
import useDebounce from '../../hooks/useDebounce';
import { ROUTES } from '../../constants/routes';
import { formatBadgeCount, getNotificationRoute } from '../../utils/notificationDisplay';
import { EmptyState, Skeleton, ViewTabs } from '../ui';
import NotificationItem from './NotificationItem';

const SEARCH_MAX_LENGTH = 100;

const LoadingRows = () => (
  <div className="divide-y divide-secondary-100 dark:divide-secondary-700" data-testid="notification-loading">
    {[0, 1, 2, 3].map((row) => (
      <div key={row} className="flex items-start gap-3 py-3 pl-5 pr-4">
        <Skeleton className="h-8 w-8 rounded-lg" />
        <div className="flex-1 space-y-2 pt-0.5">
          <Skeleton className="h-3.5 w-3/5" />
          <Skeleton className="h-3 w-11/12" />
          <Skeleton className="h-2.5 w-16" />
        </div>
      </div>
    ))}
  </div>
);

const uniqueById = (pages) => {
  const seen = new Set();
  const items = [];
  pages.forEach((page) => {
    (page?.items || []).forEach((item) => {
      if (seen.has(item.id)) return;
      seen.add(item.id);
      items.push(item);
    });
  });
  return items;
};

const NotificationPanel = ({ id, unreadCount, onClose }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 300);
  const unreadOnly = tab === 'unread';

  const feed = useNotificationFeed({ unreadOnly, search: debouncedSearch });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const dismiss = useDismissNotification();
  const { data: inboxUnread } = useInboxUnreadCount();
  const inboxUnreadCount = inboxUnread?.unread_count || 0;

  const { data, hasNextPage, isFetchingNextPage, fetchNextPage } = feed;
  const notifications = useMemo(() => uniqueById(data?.pages || []), [data]);
  const searching = search.trim() !== debouncedSearch || feed.isPlaceholderData;

  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { root: listRef.current, rootMargin: '120px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const goTo = (route) => {
    onClose();
    navigate(route);
  };

  const handleOpen = (notification) => {
    if (!notification.is_read) markRead.mutate(notification);
    const route = getNotificationRoute(notification);
    if (route) goTo(route);
  };

  const tabs = [
    { id: 'all', label: 'All' },
    {
      id: 'unread',
      label: (
        <>
          Unread
          {unreadCount > 0 && (
            <>
              {' '}
              <span className="rounded-full bg-primary-50 px-1.5 text-[11px] font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                {formatBadgeCount(unreadCount)}
              </span>
            </>
          )}
        </>
      ),
    },
  ];

  let body;
  if (feed.isLoading) {
    body = <LoadingRows />;
  } else if (feed.isError && notifications.length === 0) {
    body = (
      <div className="flex flex-col items-center gap-2 px-6 py-10 text-center" role="alert">
        <AlertCircle className="h-6 w-6 text-rose-500" aria-hidden="true" />
        <p className="text-sm text-secondary-600 dark:text-secondary-300">Couldn't load notifications.</p>
        <button
          type="button"
          onClick={() => feed.refetch()}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
        >
          Try again
        </button>
      </div>
    );
  } else if (notifications.length === 0) {
    body = debouncedSearch ? (
      <EmptyState icon={Search} title="No matches" description={`Nothing matches "${debouncedSearch}".`} />
    ) : unreadOnly ? (
      <EmptyState icon={CheckCheck} title="You're all caught up" description="No unread notifications." />
    ) : (
      <EmptyState
        icon={BellOff}
        title="No notifications yet"
        description="Task assignments, approvals and leave updates will show up here."
      />
    );
  } else {
    body = (
      <ul className="divide-y divide-secondary-100 dark:divide-secondary-700" aria-label="Notifications">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onOpen={handleOpen}
            onMarkRead={(n) => markRead.mutate(n)}
            onDismiss={(n) => dismiss.mutate(n)}
          />
        ))}
        {hasNextPage && (
          <li ref={sentinelRef} className="flex justify-center py-2">
            <button
              type="button"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-secondary-500 hover:bg-secondary-100 hover:text-secondary-700 disabled:cursor-default disabled:hover:bg-transparent dark:hover:bg-secondary-700 dark:hover:text-secondary-200"
            >
              {isFetchingNextPage && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              {isFetchingNextPage ? 'Loading…' : 'Load more'}
            </button>
          </li>
        )}
      </ul>
    );
  }

  return (
    <div
      id={id}
      role="dialog"
      aria-label="Notifications"
      data-testid="notification-panel"
      className="fixed inset-x-2 top-[4.25rem] z-50 flex max-h-[min(36rem,calc(100dvh-5.5rem))] flex-col overflow-hidden rounded-xl border border-secondary-200 bg-white shadow-xl animate-fade-in dark:border-secondary-700 dark:bg-secondary-800 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[24rem]"
    >
      <div className="flex items-center justify-between gap-2 px-4 pb-1 pt-3">
        <h2 className="text-sm font-semibold text-secondary-900 dark:text-white">Notifications</h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => markAllRead.mutate()}
            disabled={unreadCount === 0}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-default disabled:text-secondary-400 disabled:hover:bg-transparent dark:text-primary-400 dark:hover:bg-primary-900/30 dark:disabled:text-secondary-500"
          >
            <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Mark all read
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-600 sm:hidden dark:hover:bg-secondary-700"
            aria-label="Close notifications"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <ViewTabs tabs={tabs} activeId={tab} onChange={setTab} className="px-2" />

      <div className="px-3 pt-2.5 pb-2">
        <div className="relative">
          {searching ? (
            <Loader2
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-secondary-400"
              aria-hidden="true"
            />
          ) : (
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-secondary-400"
              aria-hidden="true"
            />
          )}
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxLength={SEARCH_MAX_LENGTH}
            placeholder="Search notifications"
            aria-label="Search notifications"
            className="input-field h-8 py-1 pl-8 text-sm"
          />
        </div>
      </div>

      <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-secondary-100 dark:border-secondary-700">
        {body}
      </div>

      <button
        type="button"
        onClick={() => goTo(ROUTES.INBOX)}
        className="flex items-center justify-between border-t border-secondary-100 px-4 py-2.5 text-xs font-medium text-secondary-600 transition-colors hover:bg-secondary-50 hover:text-primary-600 dark:border-secondary-700 dark:text-secondary-300 dark:hover:bg-secondary-700/50"
      >
        <span className="inline-flex items-center gap-1.5">
          <Inbox className="h-3.5 w-3.5" aria-hidden="true" />
          Announcements & messages
          {inboxUnreadCount > 0 && (
            <span className="rounded-full bg-primary-600 px-1.5 text-[10px] font-semibold text-white">
              {formatBadgeCount(inboxUnreadCount)}
            </span>
          )}
        </span>
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
};

export default NotificationPanel;
